import React, { useEffect } from "react";
import styles from "./styles.module.scss";
import { useActions, useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { clearSpace, fetchSpaceById } from "../../../actions/space";
import { useHistory, useParams } from "react-router-dom";
import { shallowEqual, useDispatch } from "react-redux";
import GettingStartedProgress from "app/components/GettingStarted/GettingStartedProgress";
import { Loading } from "app/components/Wrappers";
import SpaceHeader from "app/components/SpaceHeader";

interface Props {
  children: React.ReactNode;
}

const SpacePageLayout = ({ children }: Props) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { selectedSpace } = useSelector(
    (state: RootState) => state.space,
    shallowEqual,
  );

  const { self } = useSelector((state: RootState) => state.user, shallowEqual);

  const spaceActions = useActions({
    fetchSpaceById,
    clearSpace,
  });

  useEffect(() => {
    if (id) {
      try {
        spaceActions.fetchSpaceById(id, {
          "options[include]": ["invitations_info", "user_settings"],
        });
      } catch (error) {
        console.error("Failed to fetch space:", error);
      }
    }

    return () => {
      spaceActions.clearSpace();
    };
  }, [id, dispatch]);

  const history = useHistory();

  const route = (path: string) => {
    history.push(path);
  };

  const hasScheduleAccess = self?.kind === "coach";

  const onTimelineClick = () => {
    route(`/spaces/${id}`);
  };

  const onScheduleClick = () => {
    route(`/spaces/${id}/schedule`);
  };

  const onMembersClick = () => {
    route(`/spaces/${id}/members`);
  };

  if (!selectedSpace) {
    return <Loading isLoading loadType="spinner" />;
  }

  // CN-2829: athleteExternalId is a cache that Space fills when it is created. A
  // space created before that field existed still has it empty, so the header hid
  // the bookings menu and never loaded the athlete. For a registered athlete both
  // fields hold the same User id, so athleteId stands in. Bookings.tsx already
  // reads the pair this way.
  const athleteExternalId = selectedSpace.athleteExternalId || selectedSpace.athleteId;

  return (
    <>
      <GettingStartedProgress />
      <div className={styles.container}>
        <SpaceHeader
          title={selectedSpace?.athleteName}
          sport={selectedSpace?.sport}
          avatar={selectedSpace?.avatar}
          athleteId={selectedSpace?.athleteId}
          athleteExternalId={athleteExternalId}
          email={selectedSpace?.athleteEmail}
          phone={selectedSpace?.athletePhone}
          role={selectedSpace?.athleteAccountRole || selectedSpace?.type}
          onTimelineClick={onTimelineClick}
          onScheduleClick={onScheduleClick}
          onMembersClick={onMembersClick}
          hasScheduleAccess={hasScheduleAccess}
        />
        <div className={styles.content_container}>{children}</div>
      </div>
    </>
  );
};

export default SpacePageLayout;
